import { Injectable, inject } from '@angular/core';
import {
  Action,
  Selector,
  State,
  type StateContext,
  StateToken,
} from '@ngxs/store';
import { uniqBy, isEqual } from 'lodash-es';
import { mergeMap, tap } from 'rxjs/operators';

import { DEFAULT_FILTER_OPTIONS } from '@plugins/client/post/constants';
import { ToastService } from '@plugins/client/toast/toast.service';
import { TranslateService } from '@plugins/client/translate/services';
import type { QuestionDto } from '@plugins/faq-lib/types';
import { Filter } from '@plugins/shared/types/post/filter';
import { SortType } from '@plugins/shared/types/sort';

import { QuestionStateModel } from '../../../types';
import { TOAST_CLOSE_TIMEOUT } from '../../constants';
import { refactorAnswerComments } from '../../helpers';
import { QuestionService } from '../../services/question.service';
import { AnswersLoaded } from '../answer/answer.actions';
import { CommentsLoaded } from '../comment/comment.actions';

import {
  AnswerCreated,
  AnswerDeleted,
  ChangeQuestionsFilter,
  ChangeQuestionStatus,
  CreateQuestion,
  DeleteAllQuestions,
  DeleteFilteredQuestions,
  DeleteQuestion,
  ExportQuestions,
  GetQuestionDetails,
  LikeQuestion,
  LoadMoreQuestions,
  LoadQuestions,
  QuestionSubscribe,
  QuestionUnsubscribe,
  ResetQuestionsFilter,
  SetOverviewQuestionAnchor,
  UnsetOverviewQuestionAnchor,
  UpdateQuestion,
} from './question.actions';

const QUESTION_STATE_TOKEN = new StateToken<QuestionStateModel>('question');

export const defaultFilterValue = {
  filter: DEFAULT_FILTER_OPTIONS[0].value,
  sort: SortType.NEW_OLD,
  search: '',
};

@State<QuestionStateModel>({
  name: QUESTION_STATE_TOKEN,
  defaults: {
    questions: [],
    total: 0,
    loaded: false,
    loading: false,
    loadingMore: false,
    filter: defaultFilterValue,
    showOverviewNotice: false,
    overviewQuestionAnchor: null,
  },
})
@Injectable()
export class QuestionState {
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly questionService = inject(QuestionService);

  @Selector()
  static questions(state: QuestionStateModel) {
    return state.questions;
  }

  @Selector()
  static allLoaded(state: QuestionStateModel) {
    return state.questions.length >= state.total;
  }

  @Selector()
  static question(state: QuestionStateModel) {
    return (questionId: string) =>
      state.questions.find((q) => q.id === questionId);
  }

  @Selector()
  static loaded(state: QuestionStateModel) {
    return state.loaded;
  }

  @Selector()
  static loading(state: QuestionStateModel) {
    return state.loading;
  }

  @Selector()
  static loadingMore(state: QuestionStateModel) {
    return state.loadingMore;
  }

  @Selector()
  static total(state: QuestionStateModel) {
    return state.total;
  }

  @Selector()
  static filter(state: QuestionStateModel) {
    return state.filter;
  }

  @Selector()
  static isFilterChanged(state: QuestionStateModel) {
    return !isEqual(state.filter, defaultFilterValue);
  }

  @Selector()
  static showOverviewNotice(state: QuestionStateModel) {
    return state.showOverviewNotice;
  }

  @Selector()
  static overviewQuestionAnchor(state: QuestionStateModel) {
    return state.overviewQuestionAnchor;
  }

  @Action(LoadQuestions)
  loadQuestions(
    { getState, patchState }: StateContext<QuestionStateModel>,
    { skip }: LoadQuestions,
  ) {
    patchState({
      loaded: false,
      loading: true,
      loadingMore: false,
    });
    const { filter } = getState();
    return this.questionService.load(filter, skip ?? 0).pipe(
      tap((res) => {
        patchState({
          questions: res.questions,
          total: res.count,
          loaded: true,
          loading: false,
          showOverviewNotice: false,
        });
      }),
    );
  }

  @Action(LoadMoreQuestions)
  loadMoreQuestions({
    getState,
    patchState,
  }: StateContext<QuestionStateModel>) {
    patchState({ loadingMore: true });
    const { questions, filter } = getState();
    return this.questionService.load(filter, questions.length).pipe(
      tap((res) => {
        patchState({
          questions: uniqBy([...questions, ...res.questions], 'id'),
          loadingMore: false,
          total: res.count,
        });
      }),
    );
  }

  @Action(GetQuestionDetails)
  getQuestionDetails(
    { patchState, dispatch }: StateContext<QuestionStateModel>,
    { questionId, skipAnswers, takeAnswers }: GetQuestionDetails,
  ) {
    patchState({
      loading: true,
    });
    return this.questionService
      .getDetails(questionId, skipAnswers, takeAnswers)
      .pipe(
        mergeMap((response) => {
          const { answers: qAnswers, ...question } = response;
          const { answers, comments } = refactorAnswerComments(qAnswers);
          patchState({
            questions: [question],
            loading: false,
          });
          return dispatch([
            new AnswersLoaded(answers, question.totalAnswers),
            new CommentsLoaded(comments),
          ]);
        }),
      );
  }

  @Action(CreateQuestion)
  createQuestion(
    { dispatch }: StateContext<QuestionStateModel>,
    { question }: CreateQuestion,
  ) {
    return this.questionService.create(question).pipe(
      mergeMap(() => {
        this.showCrudMessage('create');
        return dispatch(new ResetQuestionsFilter());
      }),
    );
  }

  @Action(UpdateQuestion)
  updateQuestion(
    { getState, patchState }: StateContext<QuestionStateModel>,
    { question }: UpdateQuestion,
  ) {
    return this.questionService.update(question).pipe(
      tap((updatedQuestion) => {
        const { questions, filter } = getState();
        patchState({
          questions: questions.map((q) =>
            q.id === updatedQuestion.id ? { ...q, ...updatedQuestion } : q,
          ),
          showOverviewNotice: this.isFilterChanged(filter),
        });
        this.showCrudMessage('update');
      }),
    );
  }

  @Action(LikeQuestion)
  like(
    { getState, patchState }: StateContext<QuestionStateModel>,
    { questionId }: LikeQuestion,
  ) {
    return this.questionService.like(questionId).pipe(
      tap((vote) => {
        const { questions } = getState();
        patchState({
          questions: questions.map((q) =>
            q.id === questionId
              ? {
                  ...q,
                  liked: vote.points,
                  totalLikes: (q.totalLikes || 0) + (vote.points ? 1 : -1),
                }
              : q,
          ),
        });
      }),
    );
  }

  @Action(ChangeQuestionStatus)
  changeStatus(
    { getState, patchState }: StateContext<QuestionStateModel>,
    { questionId, status }: ChangeQuestionStatus,
  ) {
    const { questions, filter } = getState();
    patchState({
      questions: questions.map((q) =>
        q.id === questionId ? { ...q, status } : q,
      ),
      showOverviewNotice: this.isFilterChanged(filter),
    });
  }

  @Action(AnswerCreated)
  answerAdded(
    { getState, patchState }: StateContext<QuestionStateModel>,
    { questionId, subscription }: AnswerCreated,
  ) {
    const { questions } = getState();
    patchState({
      questions: questions.map((q) =>
        q.id === questionId
          ? { ...q, subscription, totalAnswers: q.totalAnswers + 1 }
          : q,
      ),
    });
  }

  @Action(AnswerDeleted)
  answerDeleted(
    { getState, patchState }: StateContext<QuestionStateModel>,
    { question }: AnswerDeleted,
  ) {
    const { questions, filter, showOverviewNotice } = getState();
    patchState(
      questions.reduce(
        (acc, curr) => {
          if (curr.id === question.id) {
            acc.questions.push({
              ...curr,
              status: question.status,
              totalAnswers: curr.totalAnswers - 1,
            });
            acc.showOverviewNotice =
              acc.showOverviewNotice ||
              (question.status !== curr.status && this.isFilterChanged(filter));
          } else {
            acc.questions.push(curr);
          }
          return acc;
        },
        { questions: [] as QuestionDto[], showOverviewNotice },
      ),
    );
  }

  @Action(DeleteQuestion)
  deleteQuestion(
    { getState, patchState }: StateContext<QuestionStateModel>,
    { questionId }: DeleteQuestion,
  ) {
    return this.questionService.delete(questionId).pipe(
      tap(() => {
        const { questions, total } = getState();
        patchState({
          questions: questions.filter((q) => q.id !== questionId),
          total: total - 1,
        });
        this.showCrudMessage('delete');
      }),
    );
  }

  @Action(DeleteFilteredQuestions)
  deleteFilteredIdeas({
    getState,
    patchState,
  }: StateContext<QuestionStateModel>) {
    const { filter } = getState();
    return this.questionService.deleteFiltered(filter).pipe(
      tap(() => {
        patchState({
          questions: [],
          total: 0,
        });
        this.showCrudMessage('deleteFiltered');
      }),
    );
  }

  @Action(DeleteAllQuestions)
  deleteAllIdeas({ patchState }: StateContext<QuestionStateModel>) {
    return this.questionService.deleteAll().pipe(
      tap(() => {
        patchState({
          questions: [],
          total: 0,
        });
        this.showCrudMessage('deleteAll');
      }),
    );
  }

  @Action(ChangeQuestionsFilter)
  changeFilter(
    { getState, patchState, dispatch }: StateContext<QuestionStateModel>,
    { filter }: ChangeQuestionsFilter,
  ) {
    const { filter: oldFilter } = getState();
    const newFilter = { ...oldFilter, ...filter };
    const prevFilter = this.questionService.transformFilter(oldFilter);
    const currFilter = this.questionService.transformFilter(newFilter);
    patchState({ filter: newFilter });
    if (!isEqual(prevFilter, currFilter)) {
      return dispatch(new LoadQuestions());
    }
  }

  @Action(ResetQuestionsFilter)
  resetFilter({ patchState, dispatch }: StateContext<QuestionStateModel>) {
    patchState({
      filter: defaultFilterValue,
    });
    return dispatch(new LoadQuestions());
  }

  @Action(ExportQuestions)
  export(
    { getState }: StateContext<QuestionStateModel>,
    { anonymousMode, profileFields }: ExportQuestions,
  ) {
    return this.questionService.export(anonymousMode, profileFields);
  }

  @Action(SetOverviewQuestionAnchor)
  setOverviewQuestionAnchor(
    { patchState }: StateContext<QuestionStateModel>,
    { questionId }: SetOverviewQuestionAnchor,
  ) {
    patchState({ overviewQuestionAnchor: questionId });
  }

  @Action(UnsetOverviewQuestionAnchor)
  unsetOverviewQuestionAnchor({
    patchState,
  }: StateContext<QuestionStateModel>) {
    patchState({ overviewQuestionAnchor: null });
  }

  private isFilterChanged(filter: Filter) {
    return !isEqual(filter, defaultFilterValue);
  }

  private showCrudMessage(key: string) {
    this.toastService.show({
      color: 'green',
      message: this.translateService.instant(`toast.${key}`),
      closeTimeout: TOAST_CLOSE_TIMEOUT,
    });
  }

  @Action(QuestionSubscribe)
  questionSubscribe(
    { getState, patchState }: StateContext<QuestionStateModel>,
    { questionId }: QuestionSubscribe,
  ) {
    return this.questionService.subscribe(questionId).pipe(
      tap((subscription) => {
        const { questions } = getState();
        patchState({
          questions: questions.map((q) =>
            q.id === questionId ? { ...q, subscription } : q,
          ),
        });
      }),
    );
  }

  @Action(QuestionUnsubscribe)
  questionUnsubscribe(
    { getState, patchState }: StateContext<QuestionStateModel>,
    { questionId }: QuestionUnsubscribe,
  ) {
    return this.questionService.unsubscribe(questionId).pipe(
      tap((subscription) => {
        const { questions } = getState();
        patchState({
          questions: questions.map((q) =>
            q.id === questionId ? { ...q, subscription } : q,
          ),
        });
      }),
    );
  }
}
